import { create, useStore } from "zustand";
import { temporal } from "zundo";
import type { TemporalState } from "zundo";
import type {
  InteractionMode,
  MeasurePoint,
  SceneObjectData,
  SceneSettings,
  TransformMode,
} from "../types";

interface SceneState {
  objects: SceneObjectData[];
  selectedId: string | null;
  settings: SceneSettings;
  transformMode: TransformMode;
  interactionMode: InteractionMode;
  measurePoints: MeasurePoint[];

  addObject: (object: SceneObjectData) => void;
  removeObject: (id: string) => void;
  updateObject: (id: string, partial: Partial<SceneObjectData>) => void;
  selectObject: (id: string | null) => void;
  setTransformMode: (mode: TransformMode) => void;
  setInteractionMode: (mode: InteractionMode) => void;
  toggleGround: () => void;
  toggleGrid: () => void;
  toggleAutoRotate: () => void;
  addMeasurePoint: (point: MeasurePoint) => void;
  clearMeasurePoints: () => void;
  loadScene: (objects: SceneObjectData[], settings?: SceneSettings) => void;
  resetScene: () => void;
}

const defaultSettings: SceneSettings = {
  groundVisible: true,
  gridVisible: true,
  autoRotate: false,
};

export const useSceneStore = create<SceneState>()(
  temporal(
    (set) => ({
      objects: [],
      selectedId: null,
      settings: defaultSettings,
      transformMode: "translate",
      interactionMode: "select",
      measurePoints: [],

      addObject: (object) =>
        set((state) => ({
          objects: [...state.objects, object],
          selectedId: object.id,
        })),

      removeObject: (id) =>
        set((state) => ({
          objects: state.objects.filter((o) => o.id !== id),
          selectedId: state.selectedId === id ? null : state.selectedId,
        })),

      updateObject: (id, partial) =>
        set((state) => ({
          objects: state.objects.map((o) => (o.id === id ? { ...o, ...partial } : o)),
        })),

      selectObject: (id) => set({ selectedId: id }),
      setTransformMode: (mode) => set({ transformMode: mode }),
      setInteractionMode: (mode) => set({ interactionMode: mode, measurePoints: [] }),

      toggleGround: () =>
        set((state) => ({ settings: { ...state.settings, groundVisible: !state.settings.groundVisible } })),
      toggleGrid: () =>
        set((state) => ({ settings: { ...state.settings, gridVisible: !state.settings.gridVisible } })),
      toggleAutoRotate: () =>
        set((state) => ({ settings: { ...state.settings, autoRotate: !state.settings.autoRotate } })),

      addMeasurePoint: (point) =>
        set((state) => ({
          measurePoints: state.measurePoints.length >= 2 ? [point] : [...state.measurePoints, point],
        })),
      clearMeasurePoints: () => set({ measurePoints: [] }),

      loadScene: (objects, settings) =>
        set({ objects, settings: settings ?? defaultSettings, selectedId: null, measurePoints: [] }),

      resetScene: () => set({ objects: [], selectedId: null, measurePoints: [] }),
    }),
    {
      limit: 50,
      partialize: (state) => ({ objects: state.objects, selectedId: state.selectedId }),
    },
  ),
);

type UndoableState = Pick<SceneState, "objects" | "selectedId">;

export function useTemporalStore<T>(selector: (state: TemporalState<UndoableState>) => T): T {
  return useStore(useSceneStore.temporal, selector);
}
